import { intersection } from 'underscore';

import { fileType } from '@ui/uploads/uploads.types';
import { FontPack, ThemePack } from '@/types/theme-packs.types';
import { PATHNAMES } from '@/utils/pathnames';
import { BLAdmin } from '@/types/profile.types';
import { EPermissions } from '@/types/permissions.types';

export const GRID_PAGE_COUNT = 15;
export const LIST_PAGE_COUNT = 15;

export const THUMBNAIL_TYPES = new Set(['pptx', 'docx', 'xlsx', 'key', 'pdf']);
export const FILE_TYPES = {
  ALL_DOCS: 0,
  PDF: 1,
  WORD: 2,
  EXCEL: 3,
  POWERPOINT: 4,
  MISC: 5,
  IMAGES: 6,
  RECORDED: 7,
  UPLOADED: 8,
  FONTS: 9,
  THEME_PACKS: 10,
  _DEPRECATED_THEMES: 11,
  TEMPLATES: 12,
  VIDEO_ARCHIVE: 13,
  PRODUCTS: 14,
};

export const FILE_TYPE_ROUTE_MAP: { [key: number]: string } = {
  0: PATHNAMES.Admin.ContentSubLevels.Documents.All,
  1: PATHNAMES.Admin.ContentSubLevels.Documents.Pdf,
  2: PATHNAMES.Admin.ContentSubLevels.Documents.Word,
  3: PATHNAMES.Admin.ContentSubLevels.Documents.Excel,
  4: PATHNAMES.Admin.ContentSubLevels.Documents.Powerpoint,
  5: PATHNAMES.Admin.ContentSubLevels.Documents.Misc,
  6: PATHNAMES.Admin.ContentSubLevels.Images,
  7: PATHNAMES.Admin.ContentSubLevels.Videos.Recorded,
  8: PATHNAMES.Admin.ContentSubLevels.Videos.Uploaded,
  9: PATHNAMES.Admin.ContentSubLevels.Fonts,
  10: PATHNAMES.Admin.ContentSubLevels.Palettes,
  11: PATHNAMES.Admin.ContentSubLevels.Themes,
  12: PATHNAMES.Admin.ContentSubLevels.Templates,
  13: PATHNAMES.Admin.ContentSubLevels.Videos.Archive,
  14: PATHNAMES.Admin.ContentSubLevels.Products,
};

export const ROUTE_FILE_TYPE_MAP: { [key: string]: number } = {
  [PATHNAMES.Admin.ContentSubLevels.Documents.All]: 0,
  [PATHNAMES.Admin.ContentSubLevels.Documents.Pdf]: 1,
  [PATHNAMES.Admin.ContentSubLevels.Documents.Word]: 2,
  [PATHNAMES.Admin.ContentSubLevels.Documents.Excel]: 3,
  [PATHNAMES.Admin.ContentSubLevels.Documents.Powerpoint]: 4,
  [PATHNAMES.Admin.ContentSubLevels.Documents.Misc]: 5,
  [PATHNAMES.Admin.ContentSubLevels.Images]: 6,
  [PATHNAMES.Admin.ContentSubLevels.Videos.Recorded]: 7,
  [PATHNAMES.Admin.ContentSubLevels.Videos.Uploaded]: 8,
  [PATHNAMES.Admin.ContentSubLevels.Fonts]: 9,
  [PATHNAMES.Admin.ContentSubLevels.Palettes]: 10,
  [PATHNAMES.Admin.ContentSubLevels.Themes]: 11,
  [PATHNAMES.Admin.ContentSubLevels.Templates]: 12,
  [PATHNAMES.Admin.ContentSubLevels.Videos.Archive]: 13,
  [PATHNAMES.Admin.ContentSubLevels.Products]: 14,
};

export const FILE_TYPE_KEYS = Object.entries(FILE_TYPES).reduce((acc: { [key: number]: string }, curr) => {
  acc[curr[1]] = curr[0];
  return acc;
}, {});

export const FILE_NUMBER_MAP: { [key: string]: number } = {
  pdf: 1,
  doc: 2,
  docx: 2,
  docm: 2,
  csv: 3,
  xls: 3,
  xla: 3,
  xlam: 3,
  xlsm: 3,
  xlsx: 3,
  ppt: 4,
  pptx: 4,
  pptm: 4,
  jpg: 6,
  png: 6,
  jpeg: 6,
  gif: 6,
  webp: 6,
  svg: 6,
  tiff: 6,
  tif: 6,
};

export const SORT_METHODS_BASE = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
  AZ: 'a-z',
  ZA: 'z-a',
};

export const SORT_METHODS_DOCUMENTS = {
  ...SORT_METHODS_BASE,
  LARGEST: 'largest',
  SMALLEST: 'smallest',
};

export const SORT_METHODS_VIDEOS = {
  ...SORT_METHODS_BASE,
  LONGEST: 'longest',
  SHORTEST: 'shortest',
};
export const SORT_METHODS_IMAGES = SORT_METHODS_VIDEOS;

export const SORT_METHODS = {
  ...SORT_METHODS_BASE,
  ...SORT_METHODS_DOCUMENTS,
  ...SORT_METHODS_VIDEOS,
};

export function sortMiscModule(list: FontPack[] | ThemePack[], sortBy: string): (FontPack | ThemePack)[] {
  return [...list].sort((a, b) => {
    const nameA = a.title.toLowerCase();
    const nameB = b.title.toLowerCase();
    const dateA = a.created_at;
    const dateB = b.created_at;

    switch (sortBy) {
      case SORT_METHODS_BASE.AZ:
        return nameA < nameB ? -1 : 1;
      case SORT_METHODS_BASE.ZA:
        return nameA > nameB ? -1 : 1;
      case SORT_METHODS_BASE.NEWEST:
        return dateA > dateB ? -1 : 1;
      case SORT_METHODS_BASE.OLDEST:
        return dateA < dateB ? -1 : 1;
      default:
        return 0;
    }
  });
}

export enum UPLOAD_TYPES {
  DOCUMENT = 'Documents',
  IMAGE = 'Images',
  VIDEO = 'Videos',
  FONT = 'Fonts',
  THEMES = 'Themes',
}

export const allowedFileTypes: { [key: string]: fileType[] } = {
  [UPLOAD_TYPES.DOCUMENT]: [
    'application/pdf',
    'application/msword',
    'video/mp4',
    'video/mov',
    'video/quicktime',
    'application/postscript',
    'application/font-sfnt',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'font/ttf',
    'font/otf',
    'font/woff',
    'application/font-woff',
    'application/xml',
    'text/xml',
  ],
  [UPLOAD_TYPES.IMAGE]: ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'],
  [UPLOAD_TYPES.VIDEO]: [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/quicktime',
    'video/mpeg',
    'video/x-ms-wmv',
    'video/x-msvideo',
    'video/3gpp',
    'video/3gpp',
  ],
};

export const getChannelsWithAdminOwnerRole = (user: BLAdmin, includeCurrent = false) => {
  const isAdminOf = Object.entries(user.channels)
    .map(([key, value]) => {
      if (intersection(value, [EPermissions.Admin, EPermissions.Owner]).length) {
        return Number(key);
      }
    })
    .filter<number>((val): val is number => val !== undefined)
    .filter((channel) => (includeCurrent ? true : channel !== user.active_channel));

  return isAdminOf;
};

export const convertMsToHMS = (duration: number): string => {
  const seconds = Math.floor((duration / 1000) % 60);
  const minutes = Math.floor((duration / (1000 * 60)) % 60);
  const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

  const h = hours < 10 ? '0' + hours : hours;
  const m = minutes < 10 ? '0' + minutes : minutes;
  const s = seconds < 10 ? '0' + seconds : seconds;

  return h + ':' + m + ':' + s;
};
